import { refreshCompanyMemberGroup } from "@store/company-member-groups/company-member-groups-thunk";
import { useAppDispatch } from "@store/store-helper";
import { useEffect, useRef } from "react";

/**
 * Fetches a group of the member again.
 *
 * @param groupId The ID of the group
 * @param applyChange Applies the change to the group as the user made it, when fetching failed
 * @returns the group as the member belongs to it now, null when they left it, or undefined when
 * that is unknown since fetching failed
 */

/**
 * Returns how to fetch a group of the member again after their role in it, or in one of its
 * projects, changed, since a role they inherit from a team may remain.
 *
 * @param memberIdentity The identity of the member
 */
export function useRefreshMemberGroup(memberIdentity) {
  const dispatch = useAppDispatch();
  // The requests in progress, which are aborted once the step is left, so that none of them can
  // change the groups another dialog shows meanwhile
  const requests = useRef(new Set());
  useEffect(() => {
    const pendingRequests = requests.current;
    return () => {
      pendingRequests.forEach(request => request.abort());
    };
  }, []);
  return async (groupId, applyChange) => {
    const request_0 = dispatch(refreshCompanyMemberGroup({
      groupId,
      userIdentity: memberIdentity
    }));
    requests.current.add(request_0);
    try {
      const {
        group
      } = await request_0.unwrap();
      return group;
    } catch (error) {
      if (!isAbortError(error)) {
        applyChange();
      }
      return undefined;
    } finally {
      requests.current.delete(request_0);
    }
  };
}

/** Whether a request failed since it was aborted, after which nothing should change anymore */
function isAbortError(error) {
  return typeof error === "object" && error !== null && "name" in error && error.name === "AbortError";
}